import { lazy, ComponentType } from 'react';

// Handle chunk loading failures after deployments by retrying once then forcing a reload
export function lazyWithRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const pageHasAlreadyReloaded = sessionStorage.getItem('page_reloaded_for_chunk') === 'true';
    
    try {
      const component = await componentImport();
      // Clear flag on successful load
      sessionStorage.removeItem('page_reloaded_for_chunk');
      return component;
    } catch (error) {
      // Check if it's a chunk loading error
      const isChunkError = 
        error instanceof Error && 
        (error.message.includes('Failed to fetch dynamically imported module') ||
         error.message.includes('Loading chunk') ||
         error.message.includes('Loading CSS chunk'));
      
      if (isChunkError && !pageHasAlreadyReloaded) {
        // Mark that we're about to reload to prevent infinite reload loop
        sessionStorage.setItem('page_reloaded_for_chunk', 'true');
        // Force a hard reload to get new chunk hashes
        window.location.reload();
        // Return a never-resolving promise since we're reloading
        return new Promise(() => {});
      }
      
      // Clear flag and throw if we've already reloaded or it's a different error
      sessionStorage.removeItem('page_reloaded_for_chunk');
      throw error;
    }
  });
}
